<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import ContactAPI from 'dashboard/api/contacts';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  inboxId: { type: [Number, String], required: true },
  enabled: { type: Boolean, default: false },
});

const { t } = useI18n();
const exclusions = ref([]);
const isLoading = ref(false);
const isSearching = ref(false);
const isSaving = ref(false);
const searchQuery = ref('');
const searchResults = ref([]);
const searchTimer = ref(null);

const excludedIds = computed(
  () => new Set(exclusions.value.map(e => e.contact_id))
);

const visibleResults = computed(() =>
  searchResults.value.filter(contact => !excludedIds.value.has(contact.id))
);

const contactLabel = contact => {
  if (!contact) return '';
  if (contact.phone_number) return `${contact.name} (${contact.phone_number})`;
  if (contact.email) return `${contact.name} (${contact.email})`;
  if (contact.identifier) return `${contact.name} (${contact.identifier})`;
  return contact.name || `#${contact.id}`;
};

const loadExclusions = async () => {
  if (!props.enabled || !props.inboxId) {
    exclusions.value = [];
    return;
  }
  isLoading.value = true;
  try {
    const { data } = await InboxesAPI.getAgentBotExclusions(props.inboxId);
    exclusions.value = data || [];
  } catch (_e) {
    exclusions.value = [];
  } finally {
    isLoading.value = false;
  }
};

const searchContacts = async query => {
  const q = query.trim();
  if (q.length < 2) {
    searchResults.value = [];
    return;
  }
  isSearching.value = true;
  try {
    const { data } = await ContactAPI.search(q);
    searchResults.value = data?.payload || data || [];
  } catch (_e) {
    searchResults.value = [];
  } finally {
    isSearching.value = false;
  }
};

const onSearchInput = event => {
  searchQuery.value = event.target.value;
  clearTimeout(searchTimer.value);
  searchTimer.value = setTimeout(() => searchContacts(searchQuery.value), 300);
};

const addContact = async contact => {
  if (!contact?.id) return;
  if (excludedIds.value.has(contact.id)) {
    useAlert(t('AGENT_BOTS.BOT_CONFIGURATION.EXCLUSIONS.ALREADY_ADDED'));
    return;
  }
  isSaving.value = true;
  try {
    const { data } = await InboxesAPI.addAgentBotExclusion(
      props.inboxId,
      contact.id
    );
    exclusions.value = [
      data,
      ...exclusions.value.filter(e => e.contact_id !== data.contact_id),
    ];
    searchQuery.value = '';
    searchResults.value = [];
    useAlert(t('AGENT_BOTS.BOT_CONFIGURATION.EXCLUSIONS.ADDED'));
  } catch (_e) {
    useAlert(t('AGENT_BOTS.BOT_CONFIGURATION.EXCLUSIONS.ADD_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const removeContact = async contactId => {
  isSaving.value = true;
  try {
    await InboxesAPI.removeAgentBotExclusion(props.inboxId, contactId);
    exclusions.value = exclusions.value.filter(e => e.contact_id !== contactId);
    useAlert(t('AGENT_BOTS.BOT_CONFIGURATION.EXCLUSIONS.REMOVED'));
  } catch (_e) {
    useAlert(t('AGENT_BOTS.BOT_CONFIGURATION.EXCLUSIONS.REMOVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

watch(
  () => [props.enabled, props.inboxId],
  () => loadExclusions(),
  { immediate: true }
);

onMounted(loadExclusions);

defineExpose({ loadExclusions });
</script>

<template>
  <div v-if="enabled" class="flex flex-col gap-3">
    <div class="relative">
      <input
        :value="searchQuery"
        type="search"
        class="w-full h-9 px-3 rounded-lg outline outline-1 outline-n-weak bg-n-background text-sm text-n-slate-12 placeholder:text-n-slate-10"
        :placeholder="
          $t('AGENT_BOTS.BOT_CONFIGURATION.EXCLUSIONS.SEARCH_PLACEHOLDER')
        "
        @input="onSearchInput"
      />
      <div
        v-if="searchQuery.trim().length >= 2"
        class="absolute z-20 mt-1 w-full max-h-56 overflow-auto rounded-lg border border-n-weak bg-n-solid-2 shadow-lg"
      >
        <p v-if="isSearching" class="px-3 py-2 text-xs text-n-slate-11">
          {{ $t('AGENT_BOTS.BOT_CONFIGURATION.EXCLUSIONS.SEARCHING') }}
        </p>
        <button
          v-for="contact in visibleResults"
          :key="contact.id"
          type="button"
          class="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-n-alpha-2"
          :disabled="isSaving"
          @click="addContact(contact)"
        >
          <Avatar
            :src="contact.thumbnail"
            :name="contact.name || '?'"
            :size="24"
            rounded-full
          />
          <span class="text-sm text-n-slate-12 truncate">{{
            contactLabel(contact)
          }}</span>
        </button>
        <p
          v-if="!isSearching && !visibleResults.length"
          class="px-3 py-2 text-xs text-n-slate-11"
        >
          {{ $t('AGENT_BOTS.BOT_CONFIGURATION.EXCLUSIONS.NO_RESULTS') }}
        </p>
      </div>
    </div>

    <div v-if="isLoading" class="text-xs text-n-slate-11">
      {{ $t('AGENT_BOTS.BOT_CONFIGURATION.EXCLUSIONS.LOADING') }}
    </div>
    <p v-else-if="!exclusions.length" class="text-xs text-n-slate-11">
      {{ $t('AGENT_BOTS.BOT_CONFIGURATION.EXCLUSIONS.EMPTY') }}
    </p>
    <ul v-else class="flex flex-col gap-2">
      <li
        v-for="item in exclusions"
        :key="item.id"
        class="flex items-center justify-between gap-2 rounded-lg outline outline-1 outline-n-weak px-3 py-2"
      >
        <div class="flex items-center gap-2 min-w-0">
          <Avatar
            :src="item.contact?.thumbnail"
            :name="item.contact?.name || '?'"
            :size="28"
            rounded-full
          />
          <div class="min-w-0">
            <p class="text-sm text-n-slate-12 truncate">
              {{ item.contact?.name }}
            </p>
            <p class="text-xs text-n-slate-11 truncate">
              {{
                item.contact?.phone_number ||
                item.contact?.email ||
                item.contact?.identifier
              }}
            </p>
          </div>
        </div>
        <NextButton
          type="button"
          icon="i-lucide-trash-2"
          xs
          faded
          ruby
          :disabled="isSaving"
          @click="removeContact(item.contact_id)"
        />
      </li>
    </ul>
  </div>
</template>
